// ---------------------------------------------------------------------------
// AudioUploader — Presigned URL upload flow for audio files
// ---------------------------------------------------------------------------

import { useState, useRef } from 'react';
import { Upload, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { useRequestUploadUrl, useConfirmUpload } from '@/hooks/use-audio';

interface AudioUploaderProps {
  questionId: number;
  languageCode: string;
  onComplete: () => void;
}

type UploadState = 'idle' | 'requesting' | 'uploading' | 'confirming' | 'done' | 'error';

export function AudioUploader({ questionId, languageCode, onComplete }: AudioUploaderProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const requestUrl = useRequestUploadUrl();
  const confirmUpload = useConfirmUpload();

  const [state, setState] = useState<UploadState>('idle');
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState('');

  async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('audio/')) {
      toast({ title: 'Please select an audio file (WAV, MP3, OGG)', variant: 'destructive' });
      return;
    }

    // Validate size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      toast({ title: 'File too large. Maximum size is 50MB.', variant: 'destructive' });
      return;
    }

    setFileName(file.name);

    try {
      // 1. Request presigned URL
      setState('requesting');
      const { uploadUrl, s3Key } = await requestUrl.mutateAsync({
        filename: file.name,
        contentType: file.type,
        languageCode,
        questionId,
      });

      // 2. Upload file to S3
      setState('uploading');
      const xhr = new XMLHttpRequest();
      await new Promise<void>((resolve, reject) => {
        xhr.upload.addEventListener('progress', (evt) => {
          if (evt.lengthComputable) {
            setProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed: ${xhr.status}`));
        });
        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      // 3. Confirm upload
      setState('confirming');
      await confirmUpload.mutateAsync({ s3Key, questionId, languageCode });

      setState('done');
      toast({ title: 'Audio uploaded successfully', variant: 'success' });
      onComplete();
    } catch {
      setState('error');
      toast({ title: 'Audio upload failed', variant: 'destructive' });
    }
  }

  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        onChange={handleFileSelected}
        className="hidden"
      />

      {state === 'idle' && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="h-4 w-4 mr-1" /> Upload Audio
        </Button>
      )}

      {(state === 'requesting' || state === 'confirming') && (
        <div className="flex items-center gap-2 text-sm text-carbon-600">
          <Loader2 className="h-4 w-4 animate-spin text-purple-500" />
          {state === 'requesting' ? 'Preparing upload...' : 'Confirming...'}
        </div>
      )}

      {state === 'uploading' && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm text-carbon-600">
            <Loader2 className="h-4 w-4 animate-spin text-purple-500" />
            Uploading {fileName}... {progress}%
          </div>
          <div className="h-1.5 bg-silver-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-purple-500 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {state === 'done' && (
        <div className="flex items-center gap-2 text-sm text-green-700">
          <CheckCircle2 className="h-4 w-4" />
          Upload complete
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setState('idle'); setProgress(0); }}
          >
            Upload another
          </Button>
        </div>
      )}

      {state === 'error' && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <AlertCircle className="h-4 w-4" />
          Upload failed
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setState('idle'); setProgress(0); }}
          >
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}
